import { useState, useEffect } from 'react';
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router";
import { db } from "../../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import * as theme from '../../theme';
import Heading from '../../components/Heading';
import ClientList from "../../components/dashboards/lists/ClientList";
import TruCard from '../../components/TruCard';
import OnboardingList from '../../components/dashboards/lists/OnboardingList';
import ResourceList from '../../components/dashboards/lists/ResourceList';
import TrainingContentContext from '../../contexts/TrainingContentContext';
import getServerTimestamp from '../../functions/endpoints/GetServerTimestamp';
import ActiveScanList from '../../components/dashboards/lists/ActiveScanList';
import ResourceItem from '../../components/dashboards/items/ResourceItem';
import WhatsNewCard from '../../components/WhatsNewCard';

export default function PractitionerDashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [practitioner, setPractitioner] = useState({});
  const [activeScansList, setActiveScansList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (currentUser.uid != null) {
      getPractitioner();
    }
  }, [currentUser]);

  useEffect(() => {
    if (practitioner) {
      getActiveScansData();
      getClients();
      getUnreadCount();
    }
  }, [practitioner]);

  async function getClients() {
    try {
      const docRef = doc(db, "practitioners", currentUser.uid, "clients", "list");
      const docSnap = await getDoc(docRef);
      const data = docSnap.data();
      setClientList(data.clients || []);
    } catch (err) {
      console.log(err.message);
    }
  }

  useEffect(() => {
    const checkTrainingCompletion = async () => {
      // Only proceed if practitioner.onboarding.training.dateCompleted is null
      if (practitioner.onboarding?.training?.dateCompleted == null) {
        // Check if all training items in TrainingContentContext are completed in practitioner.training
        const allCompleted = TrainingContentContext.every(
          (item) => practitioner.training?.[item.action]?.dateCompleted
        );

        // If all items are completed, update Firestore
        if (allCompleted) {
          try {
            const timestamp = await getServerTimestamp();
            const practitionerRef = doc(db, 'practitioners', currentUser.uid);

            const updates = {
              'onboarding.training.dateCompleted': timestamp,
              dateModified: timestamp,
            };

            // Update Firestore
            await updateDoc(practitionerRef, updates);

            // Update local practitioner state
            setPractitioner((prev) => ({
              ...prev,
              onboarding: {
                ...prev.onboarding,
                training: {
                  ...prev.onboarding?.training,
                  dateCompleted: timestamp,
                },
              },
              dateModified: timestamp,
            }));
          } catch (err) {
            console.error('Failed to update training completion status:', err.message);
          }
        }
      }
    };

    if (practitioner) {
      checkTrainingCompletion();
    }
  }, [practitioner]);

  async function getPractitioner() {
    try {
      const practitionerRef = doc(db, "practitioners", currentUser.uid);
      const docSnap = await getDoc(practitionerRef);
      setPractitioner(docSnap.data() || {});
    } catch (err) {
      console.log("Failed to get practitioner: " + err.message);
    }
  }

  async function getActiveScansData() {
    try {
      const docRef = doc(db, "activeScans", currentUser.uid);
      const docSnap = await getDoc(docRef);
      setActiveScansList(docSnap.data()?.scans || []);
    } catch (err) {
      console.error("Error fetching active scans:", err.message);
    }
  }

  async function getUnreadCount() {
    try {
      const now = new Date();
      const currentYear = now.getFullYear().toString();
      let unread = 0;

      // Fetch current year
      const currentRef = doc(db, "practitioners", currentUser.uid, "inbox", currentYear);
      const currentSnap = await getDoc(currentRef);
      if (currentSnap.exists()) {
        const threads = currentSnap.data().threads || [];
        unread += threads.filter(t => t.practitionerUnread).length;
      }

      // Fetch prior year if current month is January to June
      if (now.getMonth() < 6) {
        const priorYear = (now.getFullYear() - 1).toString();
        const priorRef = doc(db, "practitioners", currentUser.uid, "inbox", priorYear);
        const priorSnap = await getDoc(priorRef);
        if (priorSnap.exists()) {
          const threads = priorSnap.data().threads || [];
          unread += threads.filter(t => t.practitionerUnread).length;
        }
      }

      setUnreadCount(unread);
    } catch (err) {
      console.error("Error fetching unread count:", err.message);
    }
  }

  function isOnboardingComplete(practitioner) {
    if (!practitioner.onboarding || !practitioner.completedOnboarding) {
      return false;
    }
    const { training, form, cardOnFile, scheduleCall, completeCall, returnKit, personalScanReview } = practitioner.onboarding;
    return (
      !!training?.dateCompleted &&
      !!form?.dateCompleted &&
      !!cardOnFile?.dateCompleted &&
      !!scheduleCall?.dateScheduled &&
      !!completeCall?.dateCompleted &&
      !!returnKit?.dateCompleted &&
      !!personalScanReview?.dateCompleted
    );
  }

  return (
    <div style={theme.Container.flexColumn}>
      <div style={theme.Container.flexColumn}>
        <Heading
          title={`${practitioner.firstName || ''} ${practitioner.lastName || ''}`}
          subTitle={practitioner.businessName ? practitioner.businessName : null}
        />
      </div>
      <div style={theme.Container.fullMax}>
        <div style={theme.Container.flexOne}>
          <ClientList practitionerId={currentUser.uid} clientList={clientList} onboardingComplete={practitioner.completedOnboarding} cardOnFile={practitioner && practitioner.onboarding && practitioner.onboarding.cardOnFile && practitioner.onboarding.cardOnFile.dateCompleted} />
        </div>
        <div style={theme.Container.flexTwo}>
          <Heading title={'Dashboard'} subHeading={true} icon={'edit'} iconAction={() => navigate(`/practitioners/${currentUser.uid}/edit`)} />
          <div style={{ ...theme.Container.flexRow, width: '100%', gap: 20 }}>
            <div style={{ ...theme.Container.flexColumn, flex: 1, gap: 20 }}>
              {!isOnboardingComplete(practitioner) && (
                <OnboardingList practitionerId={currentUser.uid} onboarding={practitioner.onboarding ? practitioner.onboarding : {}} currentUser={currentUser} setPractitioner={setPractitioner} completedOnboarding={practitioner.completedOnboarding} />
              )}
              <ActiveScanList activeScansList={activeScansList} clientList={clientList} practitionerList={[]} isPractitioner={true} />
              <TruCard title={'Notes'} style={{ width: '100%' }}>
                {practitioner.completedOnboarding && practitioner.onboarding?.form?.dateCompleted && 
                  <div style={{ marginBottom: 10 }}>
                    <ResourceItem
                      icon="report"
                      label="Onboarding Form"
                      actionLink={() => navigate(`/practitioners/${currentUser.uid}/onboarding`)}
                    />
                  </div>
                }
                <div style={theme.TruCard.SubTextLeft}>
                  <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>General Notes:</div>
                  <div style={{ marginBottom: 20 }}>{practitioner.generalNotes || 'None provided'}</div>
                  <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Supplement Lines Added:</div>
                  <div>{practitioner.supplementLines || 'None provided'}</div>
                </div>
              </TruCard>
            </div>
            <div style={{ ...theme.Container.flexColumn, flex: 1, gap: 20 }}>
              <TruCard 
                title={'Inbox'} 
                onClick={() => navigate(`/practitioners/${currentUser.uid}/inbox`)}
              >
                <div style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100px' }}>
                  <div style={{ fontSize: '48px', fontWeight: 'bold' }}>{unreadCount}</div>
                  <div style={{ fontSize: '14px', color: 'gray' }}>Unread Messages</div>
                </div>
              </TruCard>
              <WhatsNewCard />
              <ResourceList practitionerId={currentUser.uid} isOnboarded={practitioner.completedOnboarding} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}